import React from "react";
import { useFetch } from "@/lib/api";
import { PageHero } from "@/components/blocks/PageHero";
import { Reveal } from "@/components/blocks/Reveal";
import { EmptyState } from "@/components/blocks/EmptyState";
import { RichTextContent } from "@/components/blocks/RichTextContent";
import { GalleryLightbox } from "@/components/gallery/GalleryLightbox";
import {
  SermonCard, PodcastCard, EventCard, VideoCard, GalleryCard,
} from "@/components/cards/Cards";
import { PlayCircle, Mic, Calendar, Video, Image, Megaphone } from "lucide-react";

const CONFIG = {
  sermons: { path: "/sermons", overline: "Sermons", title: "Feed on the Word", subtitle: "Recorded messages preached across the nations.", Card: SermonCard, grid: "grid gap-6 sm:grid-cols-2 lg:grid-cols-3", icon: PlayCircle, empty: "Sermons coming soon", image: "https://images.unsplash.com/photo-1497621122273-f5cfb6065c56?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" },
  podcasts: { path: "/podcasts", overline: "Podcasts", title: "Listen on the go", subtitle: "Conversations and teaching to strengthen your walk.", Card: PodcastCard, grid: "grid gap-5 md:grid-cols-2", icon: Mic, empty: "Podcasts coming soon", image: "https://images.unsplash.com/photo-1475527588268-e6a157656e35?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" },
  videos: { path: "/videos", overline: "Videos", title: "Watch the harvest", subtitle: "Crusade highlights, testimonies and teaching.", Card: VideoCard, grid: "grid gap-6 sm:grid-cols-2 lg:grid-cols-3", icon: Video, empty: "Videos coming soon", image: "/images/headers/videos-header-image1.jpg" },
  events: { path: "/events", overline: "Events", title: "Gather with us", subtitle: "Upcoming crusades, outreaches and gatherings.", Card: EventCard, grid: "grid gap-5 md:grid-cols-2", icon: Calendar, empty: "No events scheduled yet", image: "https://images.unsplash.com/photo-1566996533071-2c578080c06e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" },
  announcements: { path: "/announcements", overline: "Announcements", title: "Ministry updates", subtitle: "The latest news from Flames of Fire World Evangelism.", Card: null, grid: "space-y-4", icon: Megaphone, empty: "No announcements yet", image: "https://images.unsplash.com/photo-1497621122273-f5cfb6065c56?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" },
  gallery: { path: "/gallery", overline: "Gallery", title: "Moments in the harvest field", subtitle: "Photos from crusades, outreaches and gatherings.", Card: GalleryCard, grid: "grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4", icon: Image, empty: "Gallery coming soon", image: "https://images.unsplash.com/photo-1475527588268-e6a157656e35?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" },
};

const AnnouncementItem = ({ item }) => (
  <div className="border border-border bg-card p-6">
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-orange">
      <Megaphone className="h-4 w-4" /> {item.date || "Update"}
    </div>
    <h3 className="mt-2 font-display text-lg font-semibold text-navy">{item.title}</h3>
    {item.content && (
      <RichTextContent
        className="prose prose-sm mt-2 max-w-none text-muted-foreground"
        html={item.content}
        testId={`announcement-content-${item.id}`}
      />
    )}
  </div>
);

export const MediaListPage = ({ type }) => {
  const cfg = CONFIG[type];
  const { data, error } = useFetch(cfg.path);
  const items = data || [];

  return (
    <div>
      <PageHero
        overline={cfg.overline}
        title={cfg.title}
        subtitle={cfg.subtitle}
        image={cfg.image}
        imageTestId={`${type}-page-header-image`}
      />
      <section className="bg-white py-24 md:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          {error ? (
            <EmptyState icon={cfg.icon} title="Unable to load content" description="Something went wrong loading this page. Please refresh and try again." />
          ) : type === "events" && items.length > 0 ? (
            (() => {
              const today = new Date().toISOString().slice(0, 10);
              const upcoming = items.filter((e) => !e.date || e.date >= today);
              const past = items.filter((e) => e.date && e.date < today);
              return (
                <div className="space-y-16">
                  <div>
                    <h2 className="mb-6 font-display text-2xl font-bold text-navy">Upcoming Events</h2>
                    {upcoming.length ? (
                      <div className={cfg.grid}>{upcoming.map((e, i) => <EventCard key={i} event={e} />)}</div>
                    ) : <EmptyState icon={cfg.icon} title="No upcoming events" description="Check back soon." />}
                  </div>
                  {past.length > 0 && (
                    <div>
                      <h2 className="mb-6 font-display text-2xl font-bold text-navy">Past Events</h2>
                      <div className={`${cfg.grid} opacity-70`}>{past.map((e, i) => <EventCard key={i} event={e} />)}</div>
                    </div>
                  )}
                </div>
              );
            })()
          ) : type === "gallery" && items.length > 0 ? (
            <GalleryLightbox gridClassName={cfg.grid} items={items} />
          ) : items.length > 0 ? (
            <div className={cfg.grid}>
              {items.map((item, i) => (
                <Reveal key={i} delay={(i % 6) * 0.04}>
                  {type === "announcements" ? (
                    <AnnouncementItem item={item} />
                  ) : (
                    <cfg.Card
                      {...{ [type.slice(0, -1)]: item }}
                    />
                  )}
                </Reveal>
              ))}
            </div>
          ) : (
            <EmptyState icon={cfg.icon} title={cfg.empty} description="Content will be published here soon." />
          )}
        </div>
      </section>
    </div>
  );
};

export default MediaListPage;
